import { Button, FormControl, FormLabel, Input, Text } from "@chakra-ui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { checkPhoneDuplicate, getAuthCode } from "../../../api";
import { colors } from "../../../styles";
import { ISignUpUser } from "../../../types";
import { getApiErrorCode } from "../../../utils";
import { getSignUpErrorMessage } from "./errors";
import StepLayout, { FieldMessage, underlineInputProps } from "./StepLayout";

interface IPhoneInfoProps {
    progress: number;
    setUserData: React.Dispatch<React.SetStateAction<Partial<ISignUpUser>>>;
    onNext: () => void;
}

interface IPhoneForm {
    phone: string;
}

/* 한국 휴대폰 번호: 010 + 8자리 */

const phoneRegex = /^010\d{8}$/;

export default function PhoneInfo({
    progress,
    setUserData,
    onNext,
}: IPhoneInfoProps) {

    /* 에러 메시지, 디바운스된 휴대폰 번호 */

    const [errorMessage, setErrorMessage] = useState("");
    const [debouncedPhone, setDebouncedPhone] = useState("");

    /* useForm */

    const {
        register,
        handleSubmit,
        watch,
    } = useForm<IPhoneForm>();

    /* 휴대폰 번호 */

    const phoneValue = watch("phone") ?? "";

    const isPhoneFormatValid =
        phoneRegex.test(phoneValue);

    /* 디바운스 */

    useEffect(() => {

        const id = setTimeout(() => {
            setDebouncedPhone(phoneValue);
        }, 400);

        return () => clearTimeout(id);

    }, [phoneValue]);

    /* 휴대폰 번호 중복 확인 */

    const {
        isLoading: isCheckingPhone,
        isError: isPhoneCheckError,
        error: phoneCheckError,
    } = useQuery({
        queryKey: [
            "checkPhoneDuplicate",
            debouncedPhone,
        ],
        queryFn: () =>
            checkPhoneDuplicate({
                phone: debouncedPhone,
            }),
        enabled:
            !!debouncedPhone &&
            phoneRegex.test(debouncedPhone),
        retry: false,
    });

    /* 중복 확인 결과 */

    const isPhoneDuplicate =
        isPhoneCheckError &&
        getApiErrorCode(phoneCheckError) ===
            "PHONE_ALREADY_REGISTERED";

    const isPhoneCheckUnavailable =
        isPhoneCheckError &&
        !isPhoneDuplicate;

    /* 제출 시 최종 중복 확인 */

    const checkPhoneDuplicateMutation =
        useMutation({
            mutationFn: checkPhoneDuplicate,
            retry: false,
        });

    /* 인증번호 발송 */

    const getAuthCodeMutation =
        useMutation({
            mutationFn: getAuthCode,
            retry: false,
        });

    /* 제출 중 */

    const isSubmitting =
        checkPhoneDuplicateMutation.isPending ||
        getAuthCodeMutation.isPending;

    /* 폼 제출 */

    const onSubmit = async ({
        phone,
    }: IPhoneForm) => {

        setErrorMessage("");

        try {

            ////////////////////////////////////////////////////////////
            // 최종 중복 확인

            await checkPhoneDuplicateMutation.mutateAsync({
                phone,
            });

            ////////////////////////////////////////////////////////////
            // 인증번호 발송
            //
            // 서버에서는 인증번호 자체를 반환하지 않고
            // { ok: true, expiresIn: 180 } 정도만 반환

            await getAuthCodeMutation.mutateAsync({
                phone,
            });

            ////////////////////////////////////////////////////////////
            // 휴대폰 번호 저장

            setUserData((prev) => ({
                ...prev,
                phone,
            }));

            ////////////////////////////////////////////////////////////
            // 인증번호 입력 화면

            onNext();

        } catch (error) {

            setErrorMessage(
                getSignUpErrorMessage(
                    error,
                    "인증번호 발송 중 문제가 발생했습니다."
                )
            );
        }
    };

    /* 휴대폰 번호 안내 */

    const phoneHint = (() => {

        if (!phoneValue) {
            return null;
        }

        if (!isPhoneFormatValid) {
            return {
                message:
                    "휴대폰 번호 형식이 올바르지 않습니다.",
                tone: "error" as const,
            };
        }

        if (isCheckingPhone) {
            return {
                message: "중복 확인 중...",
                tone: "muted" as const,
            };
        }

        if (isPhoneDuplicate) {
            return {
                message:
                    "이미 가입된 휴대폰 번호입니다.",
                tone: "error" as const,
            };
        }

        if (isPhoneCheckUnavailable) {
            return {
                message:
                    "휴대폰 번호 중복 확인에 실패했습니다.",
                tone: "error" as const,
            };
        }

        return {
            message:
                "사용 가능한 휴대폰 번호입니다.",
            tone: "success" as const,
        };

    })();

    /* render */

    return (
        <StepLayout
            progress={progress}
            titles={[
                "휴대폰 번호를 인증해주세요.",
            ]}
            asForm
            onSubmit={handleSubmit(onSubmit)}
        >
            <FormControl>

                <FormLabel
                    fontSize="sm"
                    color={colors.blue}
                >
                    휴대폰 번호
                </FormLabel>

                <Input
                    {...register("phone", {
                        required: true,
                    })}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={11}
                    placeholder="휴대폰 번호 입력"
                    {...underlineInputProps}
                />

                <FieldMessage
                    message={phoneHint?.message}
                    tone={phoneHint?.tone}
                />

            </FormControl>

            <FieldMessage
                message={errorMessage}
            />

            <Button
                type="submit"
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={5}
                isLoading={isSubmitting}
                isDisabled={
                    !isPhoneFormatValid ||
                    isCheckingPhone ||
                    isPhoneCheckError
                }
            >
                <Text
                    fontSize={16}
                    fontWeight={700}
                >
                    인증번호 발송하기
                </Text>
            </Button>

        </StepLayout>
    );
}